import React, {useCallback, useEffect, useRef} from 'react';

import {DownloadButton} from 'src/components/form-buttons/download-button';
import {ToolsRibbonComponent} from 'src/components/selection-toolbar/tools-ribbon-component';
import {useUserSelection} from 'src/hooks/useUserSelection';
import {searchUsers} from 'src/openapi/admin/apis/UserSearchApi';
import {Role} from 'src/openapi/admin/models/Role';
import {Tool} from 'src/openapi/admin/models/Tool';
import {User} from 'src/openapi/admin/models/User';
import {Vendor} from 'src/openapi/admin/models/Vendor';

import {Item} from '@pb/kit-component-group';
import {Body, Table} from '@pb/kit-component-table';
import {ToolItem, ToolsRibbonGroup} from '@pb/kit-component-tools-ribbon';

import {useAppDispatch, useAppSelector} from '../../../store/hooks';
import {UserExportDownloadTool} from '../download';
import {
    accessManagementUsersEmailFieldSelector,
    accessManagementUsersPaginationSelector,
    accessManagementUsersSearchCriteriaSelector,
    accessManagementUsersSortCriteriaSelector,
    accessManagementUsersTotalCountSelector,
} from '../service/access-management-users.slice';

import {pagination as paginationCls} from './../access-management.module.styl';
import {AccessManagementPagination} from './access-management-pagination';
import {AccessManagementTableHeader} from './access-management-table-header';
import {AccessManagementTableRow} from './access-management-table-row';
import {NoResults} from './no-results';


type Props = {
    users: User[],
    hasActiveFilter: boolean,
    emailSortDir: 'ASC' | 'DESC',
    onEmailSortChange: (d: 'ASC' | 'DESC') => void,
    roleId?: string,
    onRoleIdChange: (id?: string) => void,
    vendorId?: string,
    onVendorIdChange: (id?: string) => void,
    toolId?: string,
    onToolIdChange: (id?: string) => void,
    onClearFilters?: () => void,
    roles: Role[],
    vendors: Vendor[],
    tools: Tool[],
};

export const AccessManagementTable: React.FC<Props> = ({
    users,
    hasActiveFilter,
    emailSortDir,
    onEmailSortChange,
    roleId,
    onRoleIdChange,
    vendorId,
    onVendorIdChange,
    toolId,
    onToolIdChange,
    onClearFilters,
    roles,
    vendors,
    tools,
}) => {
    const apiDispatch = useAppDispatch();
    const totalCount = useAppSelector(accessManagementUsersTotalCountSelector);
    const pagination = useAppSelector(accessManagementUsersPaginationSelector);
    const searchCriteria = useAppSelector(accessManagementUsersSearchCriteriaSelector);
    const sortCriteria = useAppSelector(accessManagementUsersSortCriteriaSelector);

    const {
        pageIds,
        selectedCount,
        allSelected,
        selection,
        selectAll,
        selectPage,
        clearAll,
        toggle,
        isSelected,
    } = useUserSelection({
        rows: users.map(u => u.userId),
        totalCount,
    });

    const emailFilter = useAppSelector(accessManagementUsersEmailFieldSelector);
    const isInitialMount = useRef(true);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        clearAll();
    }, [emailFilter, searchCriteria, sortCriteria, clearAll]);

    const pageSelectedCount = users.reduce((count, user) => count + (isSelected(user.userId) ? 1 : 0), 0);
    const isDownloadDisabled = selectedCount === 0 || totalCount === 0;

    const handleDownloadClick = useCallback(async (innerHandler: (ids?: number[]) => void) => {
        const response = await apiDispatch(searchUsers({
            userSearchRequest: {
                searchCriteria,
                sortCriteria,
                pagination: {page: 1, size: totalCount},
            },
        }));

        if (!response.payload?.success) {
            return;
        }

        const allIds = response.payload.data.users ?? [];
        let userIds: number[];

        if (!selection.inverted) {
            const selectedIds = new Set(selection.ids);
            userIds = allIds.filter(id => selectedIds.has(id));
        } else {
            const excludeSet = new Set(selection.ids);
            userIds = allIds.filter(id => !excludeSet.has(id));
        }

        innerHandler(userIds);
    }, [selection, searchCriteria, sortCriteria, totalCount, apiDispatch]);

    if (users.length === 0) {
        return (
            <NoResults
                hasActiveFilter={hasActiveFilter}
                selectedCount={selectedCount}
                onClearFilters={onClearFilters}
                emailSortDir={emailSortDir}
                onEmailSortChange={onEmailSortChange}
                roleId={roleId}
                onRoleIdChange={onRoleIdChange}
                vendorId={vendorId}
                onVendorIdChange={onVendorIdChange}
                toolId={toolId}
                onToolIdChange={onToolIdChange}
                roles={roles}
                vendors={vendors}
                tools={tools}
            />
        );
    }
    return (
        <>
            <ToolsRibbonComponent
                totalCount={totalCount}
                pageIds={pageIds}
                selectedCount={selectedCount}
                pageSelectedCount={pageSelectedCount}
                allSelected={allSelected}
                onSelectAll={selectAll}
                onSelectPage={selectPage}
                onClear={clearAll}>
                <Item>
                    <ToolsRibbonGroup>
                        <UserExportDownloadTool userIds={[]}>
                            {handler => (
                                <ToolItem>
                                    <DownloadButton
                                        disabled={isDownloadDisabled}
                                        handler={() => handleDownloadClick(handler)}
                                    />
                                </ToolItem>
                            )}
                        </UserExportDownloadTool>
                    </ToolsRibbonGroup>
                </Item>
            </ToolsRibbonComponent>
            <Table borderedColumns={true}>
                <AccessManagementTableHeader
                    totalCount={totalCount}
                    allSelected={allSelected}
                    onSelectAll={selectAll}
                    onClearAll={clearAll}
                    emailSortDir={emailSortDir}
                    onEmailSortChange={onEmailSortChange}
                    roleId={roleId}
                    onRoleIdChange={onRoleIdChange}
                    vendorId={vendorId}
                    onVendorIdChange={onVendorIdChange}
                    toolId={toolId}
                    onToolIdChange={onToolIdChange}
                    roles={roles}
                    vendors={vendors}
                    tools={tools}
                />
                <Body>
                    {users.map((user, index) => (
                        <AccessManagementTableRow
                            key={user.userId}
                            user={user}
                            index={index}
                            pageSize={pagination.size}
                            currentPage={pagination.page}
                            selected={isSelected(user.userId)}
                            onToggle={(checked, shiftKey) => toggle(user.userId, index, checked, shiftKey)}
                        />
                    ))}
                </Body>
            </Table>
            <div className={paginationCls}>
                <AccessManagementPagination/>
            </div>
        </>
    );
};
